import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import clsx from "clsx";
import "react-day-picker/style.css";
import { DayPicker } from "react-day-picker";
import { ptBR } from "react-day-picker/locale";
import { jwtDecode } from "jwt-decode";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";

import styles from "./customDashboard.module.css";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import Button from "../../../components/button/button";
import useOutsideAlerter from "../../../utils/clickOutside";
import { formatDate } from "../../../utils/formatter";
import { getDateRange, saveDateRange } from "../../../utils/localStorage";
import { useInstitution } from "../../../context/InstitutionContext";
import { getCookie } from "../../../utils/cookies";
import { toast } from "react-toastify";
import { 
  readCustomizerDashboardByUser, 
  getDashboardData,
  deactivateCustomizerDashboard,
  copyCustomizerDashboard
} from "../../../services/customDashboard";
import { getFavoriteDashboard } from "../../../services/preferences";

// Componentes
import { 
  EmptyState, 
  DashboardList, 
  DashboardCreator, 
  DashboardViewer,
  DashboardEditor 
} from "./components";

// Icons
import DashboardIcon from "../../../assets/icons/dashboard-icon.svg";
import CalendarIcon from "../../../assets/icons/calendar.svg";
import BarsFilterIcon from "../../../assets/icons/bars-filter.svg";
import MagnifyingGlassIcon from "../../../assets/icons/magnifying-glass-blue.svg";
import ArrowLeftIcon from "../../../assets/icons/arrow-left.svg";
import loadingGif from "../../../assets/loading.gif";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const FILTER_OPTIONS = {
  tipoCurso: [
    { value: "", label: "Todos os tipos" },
    { value: "graduacao", label: "Graduação" },
    { value: "pos-graduacao", label: "Pós-Graduação" },
    { value: "tecnico", label: "Técnico" },
    { value: "extensao", label: "Extensão" }
  ],
  areaConhecimento: [
    { value: "", label: "Todas as áreas" },
    { value: "exatas", label: "Ciências Exatas" },
    { value: "humanas", label: "Ciências Humanas" },
    { value: "biologicas", label: "Ciências Biológicas" },
    { value: "saude", label: "Ciências da Saúde" },
    { value: "sociais", label: "Ciências Sociais Aplicadas" },
    { value: "engenharias", label: "Engenharias" }
  ],
  curso: [
    { value: "", label: "Todos os cursos" },
    { value: "administracao", label: "Administração" },
    { value: "direito", label: "Direito" },
    { value: "engenharia-civil", label: "Engenharia Civil" },
    { value: "medicina", label: "Medicina" },
    { value: "psicologia", label: "Psicologia" }
  ],
  localidade: [
    { value: "", label: "Todas as localidades" },
    { value: "campus-central", label: "Campus Central" },
    { value: "campus-norte", label: "Campus Norte" },
    { value: "campus-sul", label: "Campus Sul" }
  ],
  modalidade: [
    { value: "", label: "Todas as modalidades" },
    { value: "presencial", label: "Presencial" },
    { value: "ead", label: "EAD" },
    { value: "hibrido", label: "Híbrido" }
  ]
};

// Rotas do módulo
const ROUTES = {
  LIST: '/app/dashboard-customizado',
  CREATE: '/app/dashboard-customizado/criar',
  VIEW: (id) => `/app/dashboard-customizado/${id}`,
  EDIT: (id) => `/app/dashboard-customizado/${id}/editar`
};

const CustomDashboard = () => {
  const { dashboardId: urlDashboardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const filtersRef = useRef(null);
  const calendarRef = useRef(null);
  const { institution } = useInstitution();

  // Determinar a view atual baseada na rota
  const isCreatingDashboard = location.pathname === ROUTES.CREATE;
  const isEditingDashboard = urlDashboardId && location.pathname.endsWith('/editar');
  const isViewingDashboard = urlDashboardId && urlDashboardId !== 'criar' && !isEditingDashboard;

  // Estados principais
  const [loading, setLoading] = useState(true);
  const [hasNoDashboard, setHasNoDashboard] = useState(false);
  const [dashboardsList, setDashboardsList] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Estados para modal de confirmação de deleção
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState(null);
  const [deletingDashboard, setDeletingDashboard] = useState(false);

  // Estados para modal de cópia de dashboard
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [dashboardToCopy, setDashboardToCopy] = useState(null);
  const [copyDashboardName, setCopyDashboardName] = useState("");
  const [copyingDashboard, setCopyingDashboard] = useState(false);

  // Estado para dashboard favorito
  const [favoriteDashboardId, setFavoriteDashboardId] = useState(null);

  // Estados de filtros para o viewer (filtros dinâmicos da API)
  const [viewerFilters, setViewerFilters] = useState({
    knowledgeArea: "",
    discipline: "",
    courseType: "",
    locality: "",
    modality: "",
    shift: ""
  });

  // Estados de filtros para o viewer (filtros estáticos - deprecated)
  const [filters, setFilters] = useState({
    tipoCurso: "",
    areaConhecimento: "",
    curso: "",
    localidade: "",
    modalidade: ""
  });

  // Estados de data
  const [dateRange, setDateRange] = useState(() => {
    return getDateRange() || { from: new Date(new Date().getFullYear(), 0, 1), to: new Date() };
  });
  const [dateRangeString, setDateRangeString] = useState("");
  const [suggestionRange, setSuggestionRange] = useState("");
  const [customMonths, setCustomMonths] = useState("24");
  const [customYears, setCustomYears] = useState("7");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  useOutsideAlerter(filtersRef, () => setIsFiltersOpen(false));
  useOutsideAlerter(calendarRef, () => setIsCalendarOpen(false));

  // Persistir data no localStorage para compartilhar entre telas
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      saveDateRange(dateRange);
    }
  }, [dateRange]);

  // Função para recarregar a lista de dashboards
  const refreshDashboardsList = async (userId = currentUserId, showLoadingState = false) => {
    if (!userId || !institution?.id) return;

    try {
      if (showLoadingState) {
        setLoading(true);
      }

      const data = await readCustomizerDashboardByUser(userId, institution.id);
      
      if (!data || (Array.isArray(data) && data.length === 0)) {
        setHasNoDashboard(true);
        setDashboardsList([]);
      } else {
        setHasNoDashboard(false);
        const dashboards = Array.isArray(data) ? data : [data];
        setDashboardsList(dashboards);
      }
    } catch (error) {
      console.error("Erro ao buscar dashboards:", error);
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  };

  // Buscar dashboards do usuário ao montar
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        
        const token = getCookie("token");
        if (!token) {
          console.error("Token não encontrado");
          setLoading(false);
          return;
        }

        const decoded = jwtDecode(token);
        const userId = decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] 
          || decoded["sub"] 
          || decoded["userId"]
          || decoded["nameid"];

        if (!userId) {
          console.error("UserId não encontrado no token");
          setLoading(false);
          return;
        }

        setCurrentUserId(userId);

        const institutionId = institution?.id;
        if (!institutionId) {
          console.error("InstitutionId não encontrado");
          setLoading(false);
          return;
        }

        const data = await readCustomizerDashboardByUser(userId, institutionId);
        
        if (!data || (Array.isArray(data) && data.length === 0)) {
          setHasNoDashboard(true);
          setDashboardsList([]);
        } else {
          setHasNoDashboard(false);
          const dashboards = Array.isArray(data) ? data : [data];
          setDashboardsList(dashboards);
        }

        // Buscar dashboard favorito
        try {
          const favoriteDashboard = await getFavoriteDashboard();
          if (favoriteDashboard) {
            setFavoriteDashboardId(favoriteDashboard);
          }
        } catch (error) {
          console.error("Erro ao buscar dashboard favorito:", error);
        }
      } catch (error) {
        console.error("Erro ao buscar dashboard:", error);
        setHasNoDashboard(true);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [institution]);

  // Extrair filtros salvos do globalFilters (pode vir do dashboard da lista ou do dashboardData da API)
  const extractSavedFilters = (source) => {
    const gf = source?.globalFilters;
    if (!gf) return {};
    return {
      knowledgeArea: gf.knowledgeArea || "",
      discipline: gf.course || "",
      courseType: gf.courseType || "",
      locality: gf.locality || "",
      modality: gf.modality || "",
      shift: gf.shift || ""
    };
  };

  // Carregar dashboard quando está na rota de visualização
  useEffect(() => {
    const loadDashboardFromUrl = async () => {
      if (isViewingDashboard && dashboardsList.length > 0 && currentUserId && institution?.id) {
      const dashboard = dashboardsList.find(
        d => (d.dashboardId || d.id) === urlDashboardId
      );
      
      if (dashboard) {
          setSelectedDashboard(dashboard);
          setLoadingDashboard(true);
          try {
            const startDate = formatDate(dateRange.from).replaceAll("/", "-");
            const endDate = formatDate(dateRange.to).replaceAll("/", "-");
            
            const data = await getDashboardData(
              urlDashboardId, 
              currentUserId, 
              institution.id,
              startDate,
              endDate,
              viewerFilters
            );
            setDashboardData(data);

            // Inicializar viewerFilters com globalFilters da resposta da API (apenas no primeiro carregamento)
            const hasAnyViewerFilter = Object.values(viewerFilters).some(v => v !== "");
            if (!hasAnyViewerFilter && data?.globalFilters) {
              const saved = extractSavedFilters(data);
              if (Object.values(saved).some(v => v !== "")) {
                setViewerFilters(saved);
              }
            }
          } catch (error) {
            console.error("Erro ao buscar dados do dashboard:", error);
            toast.error("Erro ao carregar dashboard");
          } finally {
            setLoadingDashboard(false);
          }
      } else {
        console.warn("Dashboard não encontrado:", urlDashboardId);
          navigate(ROUTES.LIST, { replace: true });
      }
    }
    };

    loadDashboardFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlDashboardId, dashboardsList, currentUserId, institution]);

  // Limpar selectedDashboard quando sair da rota de visualização
  useEffect(() => {
    if (!isViewingDashboard) {
      setSelectedDashboard(null);
      setDashboardData(null);
    }
  }, [isViewingDashboard]);

  // Atualizar string de data
  useEffect(() => {
    setDateRangeString(
      `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
    );
  }, [dateRange]);

  // Reagir às mudanças de data quando um dashboard está selecionado
  useEffect(() => {
    const fetchDashboardWithNewDates = async () => {
      if (selectedDashboard && currentUserId && institution?.id && !loadingDashboard && isViewingDashboard) {
        try {
          setLoadingDashboard(true);
          const dashboardId = selectedDashboard.dashboardId || selectedDashboard.id;
          
          const startDate = formatDate(dateRange.from).replaceAll("/", "-");
          const endDate = formatDate(dateRange.to).replaceAll("/", "-");
          
          const data = await getDashboardData(
            dashboardId, 
            currentUserId, 
            institution.id,
            startDate,
            endDate,
            viewerFilters
          );
          setDashboardData(data);
        } catch (error) {
          console.error("Erro ao buscar dados do dashboard com novas datas:", error);
          toast.error("Erro ao atualizar dashboard");
        } finally {
          setLoadingDashboard(false);
        }
      }
    };

    // Só executa se já tiver um dashboard selecionado e mudar a data
    if (selectedDashboard) {
    fetchDashboardWithNewDates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  // Handlers de filtros
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      tipoCurso: "",
      areaConhecimento: "",
      curso: "",
      localidade: "",
      modalidade: ""
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== "").length;
  };

  // Handlers de data
  const selectSuggestionRange = (suggestionRange) => {
    setSuggestionRange(suggestionRange);
    const currentYear = new Date().getFullYear();

    let selectedRange;
    switch (suggestionRange) {
      case "today":
        selectedRange = { from: new Date(), to: new Date() };
        break;
      case "last_7":
        selectedRange = {
          from: new Date(new Date().setDate(new Date().getDate() - 7)),
          to: new Date()
        };
        break;
      case "last_30": {
        const last_30_now = new Date();
        selectedRange = {
          from: new Date(last_30_now.getFullYear(), last_30_now.getMonth(), 1),
          to: new Date(last_30_now.getFullYear(), last_30_now.getMonth() + 1, 0)
        };
        break;
      }
      case "first_semester":
        selectedRange = {
          from: new Date(`${currentYear}-01-01T00:00:00`),
          to: new Date(`${currentYear}-06-30T23:59:59`)
        };
        break;
      case "second_semester":
        selectedRange = {
          from: new Date(`${currentYear}-07-01T00:00:00`),
          to: new Date(`${currentYear}-12-31T23:59:59`)
        };
        break;
      case "last_semester": {
        const today = new Date();
        const isSecondSemester = today.getMonth() >= 6;
        if (isSecondSemester) {
          selectedRange = {
            from: new Date(`${currentYear}-01-01T00:00:00`),
            to: new Date(`${currentYear}-06-30T23:59:59`)
          };
        } else {
          const lastYear = currentYear - 1;
          selectedRange = {
            from: new Date(`${lastYear}-07-01T00:00:00`),
            to: new Date(`${lastYear}-12-31T23:59:59`)
          };
        }
        break;
      }
      case "last_year": {
        const now = new Date();
        selectedRange = {
          from: new Date(now.getFullYear() - 1, now.getMonth(), 1),
          to: new Date(now.getFullYear(), now.getMonth(), 0)
        };
        break;
      }
      case "last_x_months": {
        const monthsBack = parseInt(customMonths, 10);
        if (!isNaN(monthsBack) && monthsBack > 0) {
          const now_lxm = new Date();
          selectedRange = {
            from: new Date(now_lxm.getFullYear(), now_lxm.getMonth() - monthsBack, now_lxm.getDate()),
            to: new Date()
          };
        } else {
          return;
        }
        break;
      }
      case "current_year":
        selectedRange = {
          from: new Date(currentYear, 0, 1),
          to: new Date()
        };
        break;
      case "previous_year": {
        const previousYear = currentYear - 1;
        selectedRange = {
          from: new Date(`${previousYear}-01-01T00:00:00`),
          to: new Date(`${previousYear}-12-31T23:59:59`)
        };
        break;
      }
      case "last_5_years":
        selectedRange = {
          from: new Date(new Date().setFullYear(new Date().getFullYear() - 5)),
          to: new Date()
        };
        break;
      case "last_x_years": {
        const yearsBack = parseInt(customYears, 10);
        if (!isNaN(yearsBack) && yearsBack > 0) {
          const startYear = currentYear - yearsBack;
          selectedRange = {
            from: new Date(`${startYear}-01-01T00:00:00`),
            to: new Date(`${currentYear - 1}-12-31T23:59:59`)
          };
        } else {
          return;
        }
        break;
      }
      default:
        selectedRange = {
          from: new Date(new Date().setMonth(new Date().getMonth() - 3)),
          to: new Date(new Date().setMonth(new Date().getMonth() - 0))
        };
        break;
    }

    setDateRange(selectedRange);
    setDateRangeString(
      `${formatDate(selectedRange.from)} - ${formatDate(selectedRange.to)}`
    );
    setIsCalendarOpen(false);
  };

  const selectManualRange = () => {
    setSuggestionRange("");
    setIsCalendarOpen(false);
  };

  const handleClearDateSelection = () => {
    setSuggestionRange("");
    setDateRange({
      from: new Date(),
      to: new Date()
    });
  };

  // Handler para mudanças nos filtros do viewer
  const handleViewerFiltersChange = async (newFilters) => {
    setViewerFilters(newFilters);
    
    // Buscar dados do dashboard com os novos filtros
    if (selectedDashboard && currentUserId && institution?.id) {
      try {
        setLoadingDashboard(true);
        const dashboardId = selectedDashboard.dashboardId || selectedDashboard.id;
        
        const startDate = formatDate(dateRange.from).replaceAll("/", "-");
        const endDate = formatDate(dateRange.to).replaceAll("/", "-");
        
        const data = await getDashboardData(
          dashboardId, 
          currentUserId, 
          institution.id,
          startDate,
          endDate,
          newFilters
        );
        setDashboardData(data);
      } catch (error) {
        console.error("Erro ao buscar dados do dashboard com filtros:", error);
        toast.error("Erro ao aplicar filtros");
      } finally {
        setLoadingDashboard(false);
      }
    }
  };

  // Handlers de navegação
  const handleStartCreating = () => {
    navigate(ROUTES.CREATE);
  };

  const handleCancelCreating = () => {
    navigate(ROUTES.LIST);
  };

  const handleSelectDashboard = (dashboard) => {
      const dashboardId = dashboard.dashboardId || dashboard.id;
    navigate(ROUTES.VIEW(dashboardId));
  };

  const handleBackToList = () => {
    navigate(ROUTES.LIST);
  };

  const handleDashboardCreated = async (createdDashboard, data) => {
    setHasNoDashboard(false);
    
    // Recarrega a lista de dashboards da API
    await refreshDashboardsList();
    
    // Navegar para a visualização ou lista
    if (createdDashboard) {
      if (data && createdDashboard.dashboardId) {
        setSelectedDashboard(createdDashboard);
        setDashboardData(data);
        navigate(ROUTES.VIEW(createdDashboard.dashboardId));
      } else {
        navigate(ROUTES.LIST);
      }
    } else {
      navigate(ROUTES.LIST);
    }
  };

  // Handlers para ações do menu do dashboard
  const handleDeleteDashboard = (dashboard) => {
    setDashboardToDelete(dashboard);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!dashboardToDelete || !currentUserId) return;

    const dashboardId = dashboardToDelete.dashboardId || dashboardToDelete.id;
    setDeletingDashboard(true);

    try {
      await deactivateCustomizerDashboard(dashboardId, currentUserId);
      toast.success(`Dashboard "${dashboardToDelete.name}" deletado com sucesso!`);
      
      // Recarrega a lista de dashboards da API
      await refreshDashboardsList();

      setShowDeleteModal(false);
      setDashboardToDelete(null);
    } catch (error) {
      console.error("Erro ao deletar dashboard:", error);
      toast.error("Erro ao deletar dashboard. Tente novamente.");
    } finally {
      setDeletingDashboard(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDashboardToDelete(null);
  };

  const handleEditDashboard = (dashboard) => {
    const dashboardId = dashboard.dashboardId || dashboard.id;
    navigate(ROUTES.EDIT(dashboardId));
  };

  const handleCancelEditing = () => {
    navigate(ROUTES.LIST);
  };

  const handleDashboardUpdated = async () => {
    // Recarrega a lista de dashboards da API
    await refreshDashboardsList();
    // Navegar para a lista
    navigate(ROUTES.LIST);
  };

  const handleCopyDashboard = (dashboard) => {
    setDashboardToCopy(dashboard);
    setCopyDashboardName(`${dashboard.name} (Cópia)`);
    setShowCopyModal(true);
  };

  const handleConfirmCopy = async () => {
    if (!dashboardToCopy || !currentUserId || !copyDashboardName.trim()) return;

    const sourceDashboardId = dashboardToCopy.dashboardId || dashboardToCopy.id;
    setCopyingDashboard(true);

    try {
      await copyCustomizerDashboard(
        sourceDashboardId,
        currentUserId,  // targetUserId - copia para o usuário atual
        currentUserId,  // copiedByUserId - quem está copiando
        copyDashboardName.trim()
      );

      toast.success(`Dashboard copiado com sucesso!`);
      
      // Recarrega a lista de dashboards da API
      await refreshDashboardsList();

      setShowCopyModal(false);
      setDashboardToCopy(null);
      setCopyDashboardName("");
    } catch (error) {
      console.error("Erro ao copiar dashboard:", error);
      toast.error("Erro ao copiar dashboard. Tente novamente.");
    } finally {
      setCopyingDashboard(false);
    }
  };

  const handleCancelCopy = () => {
    setShowCopyModal(false);
    setDashboardToCopy(null);
    setCopyDashboardName("");
  };

  // Estado de loading inicial
  if (loading) {
    return (
      <div className={styles.customDashboardWrapper}>
        <HeaderTitle title="Dashboard Customizado" icon={DashboardIcon} />
        <div className={styles.dashboardContent}>
          <div className={styles.emptyState}>
            <p>Carregando dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Modo de criação de dashboard (rota: /criar)
  if (isCreatingDashboard) {
    return (
      <DashboardCreator
        onCancel={handleCancelCreating}
        onDashboardCreated={handleDashboardCreated}
        currentUserId={currentUserId}
        dateRange={dateRange}
      />
    );
  }

  // Modo de edição de dashboard (rota: /:dashboardId/editar)
  if (isEditingDashboard && urlDashboardId) {
    return (
      <DashboardEditor
        dashboardId={urlDashboardId}
        onCancel={handleCancelEditing}
        onDashboardUpdated={handleDashboardUpdated}
        currentUserId={currentUserId}
        dateRange={dateRange}
      />
    );
  }

  // Visualização de dashboard (rota: /:dashboardId)
  if (isViewingDashboard && selectedDashboard) {
  return (
    <div className={styles.customDashboardWrapper}>
      <HeaderTitle title="Dashboard Customizado" icon={DashboardIcon}>
        <div className={styles.headerActions}>
            <Button
              icon={ArrowLeftIcon}
              text="Voltar"
              size="normal"
              variation="outline"
              onClick={handleBackToList}
            />
          <div className={styles.filterButtonWrapper}>
            <Button
              icon={CalendarIcon}
              text={dateRangeString}
              size="normal"
              variation="outline"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            />

            {isCalendarOpen && (
              <div ref={calendarRef} className={styles.calendarDropdown}>
                <div className={styles.suggestions}>
                  <ul>
                    <li
                      onClick={() => !loadingDashboard && selectSuggestionRange("today")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "today"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Hoje
                    </li>
                    <li
                      onClick={() => !loadingDashboard && selectSuggestionRange("last_7")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_7"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 7 dias
                    </li>
                    <li
                      onClick={() => !loadingDashboard && selectSuggestionRange("last_30")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_30"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Último mês
                    </li>
                    <li
                      onClick={() => !loadingDashboard && selectSuggestionRange("first_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "first_semester"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Primeiro Semestre
                    </li>
                    <li
                      onClick={() => !loadingDashboard && selectSuggestionRange("second_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "second_semester"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Segundo Semestre
                    </li>
                    <li
                      onClick={() => !loadingDashboard && selectSuggestionRange("last_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_semester"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Último Semestre
                    </li>
                    <li
                      onClick={() => !loadingDashboard && selectSuggestionRange("last_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_year"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 12 meses
                    </li>
                    <li
                      className={clsx(styles.suggestionsItem, styles.customMonthsItem, {
                        [styles.active]: suggestionRange === "last_x_months"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1 }}
                    >
                      <span>Últimos</span>
                      <input
                        type="number"
                        min="1"
                        className={styles.customMonthsInput}
                        value={customMonths}
                        placeholder="24"
                        onChange={(e) => setCustomMonths(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !loadingDashboard && customMonths) {
                            selectSuggestionRange("last_x_months");
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={loadingDashboard}
                      />
                      <span
                        style={{ cursor: loadingDashboard || !customMonths ? 'default' : 'pointer' }}
                        onClick={() => {
                          if (!loadingDashboard && customMonths) {
                            selectSuggestionRange("last_x_months");
                          }
                        }}
                      >
                        meses
                      </span>
                    </li>
                    <li
                      onClick={() => !loadingDashboard && selectSuggestionRange("current_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "current_year"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Ano Atual
                    </li>
                    <li
                      onClick={() => !loadingDashboard && selectSuggestionRange("previous_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "previous_year"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Ano Anterior
                    </li>
                    <li
                      onClick={() => !loadingDashboard && selectSuggestionRange("last_5_years")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_5_years"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 5 Anos
                    </li>
                    <li
                      className={clsx(styles.suggestionsItem, styles.customMonthsItem, {
                        [styles.active]: suggestionRange === "last_x_years"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1 }}
                    >
                      <span>Últimos</span>
                      <input
                        type="number"
                        min="1"
                        className={styles.customMonthsInput}
                        value={customYears}
                        placeholder="7"
                        onChange={(e) => setCustomYears(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !loadingDashboard && customYears) {
                            selectSuggestionRange("last_x_years");
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={loadingDashboard}
                      />
                      <span
                        style={{ cursor: loadingDashboard || !customYears ? 'default' : 'pointer' }}
                        onClick={() => {
                          if (!loadingDashboard && customYears) {
                            selectSuggestionRange("last_x_years");
                          }
                        }}
                      >
                        anos
                      </span>
                    </li>
                    <li
                      onClick={() => !loadingDashboard && selectSuggestionRange("manual")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "manual"
                      })}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Personalizado
                    </li>
                  </ul>
                  <div>
                    <div
                      onClick={() => !loadingDashboard && handleClearDateSelection()}
                      className={clsx(styles.suggestionsItem, styles.active)}
                      style={{ opacity: loadingDashboard ? 0.6 : 1, cursor: loadingDashboard ? 'not-allowed' : 'pointer' }}
                    >
                      Limpar seleção
                    </div>
                    <div
                      onClick={() => !loadingDashboard && selectManualRange()}
                      className={clsx(
                        styles.suggestionsItem,
                        styles.active,
                        styles["btn-search-data"]
                      )}
                      style={{
                        opacity: loadingDashboard ? 0.8 : 1,
                        cursor: loadingDashboard ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {loadingDashboard ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <img
                            src={loadingGif}
                            alt="Carregando..."
                            style={{ width: "16px", height: "16px" }}
                          />
                          <span>Carregando...</span>
                        </div>
                      ) : (
                        <>
                          <img src={MagnifyingGlassIcon} alt="magnifying-glass" />
                          Buscar dados
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.dateRangeGroup}>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Início</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.from}
                      defaultMonth={dateRange?.from}
                      onSelect={(from) => setDateRange((prev) => ({ ...prev, from }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Fim</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.to}
                      defaultMonth={dateRange?.to}
                      onSelect={(to) => setDateRange((prev) => ({ ...prev, to }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.filterButtonWrapper}>
            <Button
              icon={BarsFilterIcon}
              text={getActiveFiltersCount() > 0 ? `Filtrar (${getActiveFiltersCount()})` : "Filtrar"}
              size="normal"
              variation="outline"
              onClick={() => setIsFiltersOpen(!isFiltersOpen)}
              disabled={true}
            />

            {isFiltersOpen && (
              <div ref={filtersRef} className={styles.filtersDropdown}>
                <div className={styles.filtersHeader}>
                  <h3>Filtros</h3>
                </div>

                <div className={styles.filtersBody}>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Tipo de Curso</label>
                    <select
                      className={styles.filterSelect}
                      value={filters.tipoCurso}
                      onChange={(e) => handleFilterChange("tipoCurso", e.target.value)}
                    >
                      {FILTER_OPTIONS.tipoCurso.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Área de Conhecimento</label>
                    <select
                      className={styles.filterSelect}
                      value={filters.areaConhecimento}
                      onChange={(e) => handleFilterChange("areaConhecimento", e.target.value)}
                    >
                      {FILTER_OPTIONS.areaConhecimento.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Curso</label>
                    <select
                      className={styles.filterSelect}
                      value={filters.curso}
                      onChange={(e) => handleFilterChange("curso", e.target.value)}
                    >
                      {FILTER_OPTIONS.curso.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Localidade</label>
                    <select
                      className={styles.filterSelect}
                      value={filters.localidade}
                      onChange={(e) => handleFilterChange("localidade", e.target.value)}
                    >
                      {FILTER_OPTIONS.localidade.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Modalidade</label>
                    <select
                      className={styles.filterSelect}
                      value={filters.modalidade}
                      onChange={(e) => handleFilterChange("modalidade", e.target.value)}
                    >
                      {FILTER_OPTIONS.modalidade.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={styles.filtersFooter}>
                  <button className={styles.clearFiltersBtn} onClick={handleClearFilters}>
                    Limpar filtros
                  </button>
                  <button className={styles.applyFiltersBtn} onClick={() => setIsFiltersOpen(false)}>
                    Aplicar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </HeaderTitle>

        <DashboardViewer
          selectedDashboard={selectedDashboard}
          dashboardData={dashboardData}
          loadingDashboard={loadingDashboard}
          onFiltersChange={handleViewerFiltersChange}
          savedFilters={extractSavedFilters(dashboardData || selectedDashboard)}
        />
          </div>
    );
  }

  // Estado sem dashboards (empty state) - rota base
  if (hasNoDashboard) {
    return (
      <div className={styles.customDashboardWrapper}>
        <HeaderTitle title="Dashboard Customizado" icon={DashboardIcon} />
        <div className={styles.dashboardContent}>
          <EmptyState onStartCreating={handleStartCreating} />
        </div>
      </div>
    );
  }

  // Lista de dashboards - rota base com dashboards existentes
  return (
    <div className={styles.customDashboardWrapper}>
      <HeaderTitle title="Dashboard Customizado" icon={DashboardIcon} />
      <DashboardList
        dashboardsList={dashboardsList}
        onSelectDashboard={handleSelectDashboard}
        onStartCreating={handleStartCreating}
        onDeleteDashboard={handleDeleteDashboard}
        onEditDashboard={handleEditDashboard}
        onCopyDashboard={handleCopyDashboard}
        currentUserId={currentUserId}
        favoriteDashboardId={favoriteDashboardId}
        onFavoriteChange={setFavoriteDashboardId}
      />

      {/* Modal de confirmação de deleção */}
      {showDeleteModal && (
        <div className={styles.modalBackdrop} onClick={handleCancelDelete}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeaderNew}>
              <h3>Deletar Dashboard</h3>
              <button className={styles.closeButtonNew} onClick={handleCancelDelete}>
                ×
              </button>
          </div>

            <div className={styles.deleteModalContent}>
              <div className={styles.deleteModalIcon}>🗑️</div>
              <p className={styles.deleteModalText}>
                Tem certeza que deseja deletar o dashboard <strong>"{dashboardToDelete?.name}"</strong>?
              </p>
              <p className={styles.deleteModalWarning}>
                Esta ação não pode ser desfeita.
              </p>
            </div>

            <div className={styles.modalActionsNew}>
              <Button
                text="Cancelar"
                variation="secondary"
                size="medium"
                onClick={handleCancelDelete}
                disabled={deletingDashboard}
                styling={{
                  padding: "8px 20px",
                  fontSize: "13px"
                }}
              />
              <Button
                text={deletingDashboard ? "Deletando..." : "Deletar"}
                variation="danger"
                size="medium"
                onClick={handleConfirmDelete}
                disabled={deletingDashboard}
                styling={{
                  padding: "8px 20px",
                  fontSize: "13px"
                }}
              />
              </div>
          </div>
          </div>
        )}

      {/* Modal de cópia de dashboard */}
      {showCopyModal && (
        <div className={styles.modalBackdrop} onClick={handleCancelCopy}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeaderNew}>
              <h3>Copiar Dashboard</h3>
              <button className={styles.closeButtonNew} onClick={handleCancelCopy}>
                ×
              </button>
            </div>

            <p className={styles.modalDescription}>
              Uma cópia do dashboard <strong>"{dashboardToCopy?.name}"</strong> será criada para você.
            </p>

            <div className={styles.formGroup}>
              <label htmlFor="copy-dashboard-name">Nome do novo dashboard *</label>
              <input
                id="copy-dashboard-name"
                type="text"
                className={styles.input}
                placeholder="Digite o nome do dashboard"
                value={copyDashboardName}
                onChange={(e) => setCopyDashboardName(e.target.value)}
                maxLength={100}
                autoFocus
              />
            </div>

            <div className={styles.modalActionsNew}>
              <Button
                text="Cancelar"
                variation="secondary"
                size="medium"
                onClick={handleCancelCopy}
                disabled={copyingDashboard}
                styling={{
                  padding: "8px 20px",
                  fontSize: "13px"
                }}
              />
              <Button
                text={copyingDashboard ? "Copiando..." : "Copiar"}
                variation="primary"
                size="medium"
                onClick={handleConfirmCopy}
                disabled={copyingDashboard || !copyDashboardName.trim()}
                styling={{
                  padding: "8px 20px",
                  fontSize: "13px"
                }}
              />
              </div>
          </div>
          </div>
        )}
    </div>
  );
};

export default CustomDashboard;
